import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridRenderCellParams, GridColDef } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import { 
  useUserPortfolioLpoaFetch, 
  useLpoaEnvelopeCreate,
  PortfolioLpoa 
} from '../../../clients/graphql/pages/lpoa-queries';

interface GroupedLpoaRecord {
  id: string;
  envelopeId: string | null;
  portfolioCount: number;
  signedAt: Date | null;
  rejectedAt: Date | null;
  revokedAt: Date | null;
  rejectedReason: string | null;
  activatedAt: Date | null;
  createdAt: Date;
  records: PortfolioLpoa[];
}

export const LPOAPage = () => {
  const [searchUserKey, setSearchUserKey] = useState<string>('');
  const [userKey, setUserKey] = useState<number | undefined>(undefined);
  const [selectedLpoa, setSelectedLpoa] = useState<GroupedLpoaRecord | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [envelopeDialogOpen, setEnvelopeDialogOpen] = useState(false);
  const [envelopeId, setEnvelopeId] = useState('');
  const [brokerName, setBrokerName] = useState('');
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [resendEnvelopeData, setResendEnvelopeData] = useState<GroupedLpoaRecord | null>(null);
  const [selectedPortfolioKeys, setSelectedPortfolioKeys] = useState<Set<number>>(new Set());
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const { loading, error, data, refetch } = useUserPortfolioLpoaFetch({ userKey });
  const [createEnvelope] = useLpoaEnvelopeCreate();
  
  const lpoaData = data?.UserPortfolioLpoaFetch || [];

  // Group LPOA records by envelope ID
  const groupedLpoaData = React.useMemo(() => {
    const grouped: Record<string, PortfolioLpoa[]> = {};
    
    lpoaData.forEach((lpoa: PortfolioLpoa) => {
      const envelopeId = lpoa.envelopeId || 'no-envelope';
      if (!grouped[envelopeId]) {
        grouped[envelopeId] = [];
      }
      grouped[envelopeId].push(lpoa);
    });

    return Object.entries(grouped).map(([envelopeId, records]): GroupedLpoaRecord => {
      const firstRecord = records[0];
      return {
        id: envelopeId,
        envelopeId: envelopeId === 'no-envelope' ? null : envelopeId,
        portfolioCount: records.length,
        signedAt: firstRecord.signedAt,
        rejectedAt: firstRecord.rejectedAt,
        revokedAt: firstRecord.revokedAt,
        rejectedReason: firstRecord.rejectedReason,
        activatedAt: firstRecord.activatedAt,
        createdAt: firstRecord.createdAt,
        records: records,
      };
    });
  }, [lpoaData]);

  const handleSearch = () => {
    const key = parseInt(searchUserKey);
    if (!isNaN(key)) {
      setUserKey(key);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleViewDetails = (groupedRecord: GroupedLpoaRecord) => {
    setSelectedLpoa(groupedRecord);
    setOpenDialog(true);
  };

  const handleCreateEnvelope = async () => {
    if (envelopeId && brokerName) {
      try {
        const result = await createEnvelope({
          variables: {
            input: {
              envelopeId,
              brokerName,
            },
          },
        });

        if (result.data?.LpoaEnvelopeCreate.success) {
          setSnackbar({ open: true, message: result.data.LpoaEnvelopeCreate.message, severity: 'success' });
          setEnvelopeDialogOpen(false);
          setEnvelopeId('');
          setBrokerName('');
          refetch();
        } else {
          setSnackbar({ open: true, message: result.data?.LpoaEnvelopeCreate.message || 'Failed to create envelope', severity: 'error' });
        }
      } catch (err) {
        setSnackbar({ open: true, message: 'Error creating envelope: ' + err, severity: 'error' });
      }
    }
  };

  const getStatusChip = (record: GroupedLpoaRecord) => {
    if (record.revokedAt) {
      return <Chip label="Revoked" size="small" color="error" />;
    } else if (record.rejectedAt) {
      return <Chip label="Rejected" size="small" color="error" />;
    } else if (record.activatedAt) {
      return <Chip label="Active" size="small" color="success" />;
    } else if (record.signedAt) {
      return <Chip label="Signed" size="small" color="info" />;
    } else {
      return <Chip label="Pending" size="small" color="warning" />;
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'envelopeId',
      headerName: 'Envelope ID',
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || 'No envelope ID'}>
          <Typography variant="body2" sx={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap' 
          }}>
            {params.value || 'N/A'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'portfolioCount',
      headerName: 'Portfolios',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value} portfolio{params.value !== 1 ? 's' : ''}
        </Typography>
      ),
    },
    {
      field: 'brokerName',
      headerName: 'Broker',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.row.records?.[0]?.portfolio?.brokerName || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => getStatusChip(params.row),
    },
    {
      field: 'signedAt',
      headerName: 'Signed At',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'rejectedAt',
      headerName: 'Rejected At',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'revokedAt',
      headerName: 'Revoked At',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'activatedAt',
      headerName: 'Activated At',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'viewDetails',
      headerName: 'View Details',
      width: 130,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Tooltip title="View Portfolio Details">
            <IconButton 
              size="medium" 
              color="primary"
              onClick={() => handleViewDetails(params.row)}
            >
              <VisibilityIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'resendEnvelope',
      headerName: 'Resend Envelope',
      width: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const envelopeId = params.row.envelopeId;

        if (!envelopeId) {
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                N/A
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <Tooltip title="Resend Envelope">
              <IconButton
                size="medium"
                color="secondary"
                onClick={() => {
                  setResendEnvelopeData(params.row);
                  // Only pre-select portfolios that are not activated
                  const nonActivatedKeys = params.row.records
                    .filter((r: PortfolioLpoa) => !r.activatedAt)
                    .map((r: PortfolioLpoa) => r.portfolioKey);
                  setSelectedPortfolioKeys(new Set(nonActivatedKeys));
                  setResendModalOpen(true);
                }}
              >
                <SendIcon fontSize="medium" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', mt: 4, px: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Limited Power of Attorney (LPOA)
        </Typography>
      </Box>

      <Paper sx={{ mb: 3, p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            label="User Key"
            variant="outlined"
            value={searchUserKey}
            onChange={(e) => setSearchUserKey(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter user key..."
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSearch}
                    edge="end"
                    disabled={!searchUserKey}
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={!userKey}
          >
            Refresh
          </Button>
        </Box>
        
        {userKey && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Showing LPOA records for User Key: {userKey}
          </Typography>
        )}
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error fetching LPOA data: {error.message}
        </Alert>
      )}

      {!loading && !error && userKey && (
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={groupedLpoaData}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            rowHeight={50}
            sx={{
              '& .MuiDataGrid-row': {
                fontSize: '17px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                },
              },
              '& .MuiDataGrid-row:hover': {
                outline: '2px solid #1976d2',
                outlineOffset: '-2px',
                cursor: 'pointer',
              },
              '& .MuiDataGrid-columnHeaders': {
                fontSize: '17px',
                fontWeight: 600,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              },
            }}
            disableColumnMenu
            disableRowSelectionOnClick
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </Box>
      )}

      {!loading && !error && !userKey && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Enter a User Key to view LPOA records
          </Typography>
        </Box>
      )}

      {!loading && !error && userKey && groupedLpoaData.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No LPOA records found for User Key: {userKey}
          </Typography>
        </Box>
      )}

      {/* Details Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Envelope Details</DialogTitle>
        <DialogContent>
          {selectedLpoa && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Envelope Information
              </Typography>
              <Box sx={{ p: 2, mb: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                  <strong>Envelope ID:</strong> {selectedLpoa.envelopeId || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                  <strong>Total Portfolios:</strong> {selectedLpoa.portfolioCount}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                  <strong>Created:</strong> {new Date(selectedLpoa.createdAt).toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                  <strong>Signed:</strong> {selectedLpoa.signedAt ? new Date(selectedLpoa.signedAt).toLocaleString() : 'Not signed'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                  <strong>Activated:</strong> {selectedLpoa.activatedAt ? new Date(selectedLpoa.activatedAt).toLocaleString() : 'Not activated'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                  <strong>Rejected:</strong> {selectedLpoa.rejectedAt ? new Date(selectedLpoa.rejectedAt).toLocaleString() : 'Not rejected'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                  <strong>Revoked:</strong> {selectedLpoa.revokedAt ? new Date(selectedLpoa.revokedAt).toLocaleString() : 'Not revoked'}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {getStatusChip(selectedLpoa)}
                </Box>
              </Box>

              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Portfolio Details
              </Typography>
              {selectedLpoa.records?.map((record: PortfolioLpoa, index: number) => (
                <Box 
                  key={record.portfolioLpoaKey} 
                  sx={{ 
                    p: 2, 
                    mb: 2, 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1,
                    backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.02)'
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                    Portfolio #{index + 1}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
                    <strong>LPOA Key:</strong> {record.portfolioLpoaKey}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
                    <strong>Portfolio Key:</strong> {record.portfolioKey}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
                    <strong>Portfolio Name:</strong> {record.portfolio?.nickname || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
                    <strong>Account ID:</strong> {record.portfolio?.accountId || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
                    <strong>Broker:</strong> {record.portfolio?.brokerName || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    <strong>Account Type:</strong> {record.portfolio?.accountType || 'N/A'}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Envelope Dialog */}
      <Dialog open={envelopeDialogOpen} onClose={() => setEnvelopeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create LPOA Envelope</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Envelope ID"
              variant="outlined"
              value={envelopeId}
              onChange={(e) => setEnvelopeId(e.target.value)}
              fullWidth
            />
            <TextField
              label="Broker Name"
              variant="outlined"
              value={brokerName}
              onChange={(e) => setBrokerName(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnvelopeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateEnvelope}
            variant="contained"
            disabled={!envelopeId || !brokerName}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Envelope Dialog */}
      <Dialog
        open={resendModalOpen}
        onClose={() => setResendModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Resend Envelope - Select Portfolios</DialogTitle>
        <DialogContent>
          {resendEnvelopeData && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                Envelope ID: {resendEnvelopeData.envelopeId}
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                Select which portfolios to resend this envelope to:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {resendEnvelopeData.records?.map((record: PortfolioLpoa) => {
                  const isActivated = !!record.activatedAt;
                  return (
                    <FormControlLabel
                      key={record.portfolioKey}
                      disabled={isActivated}
                      control={
                        <Checkbox
                          checked={selectedPortfolioKeys.has(record.portfolioKey)}
                          disabled={isActivated}
                          onChange={(e) => {
                            const newSet = new Set(selectedPortfolioKeys);
                            if (e.target.checked) {
                              newSet.add(record.portfolioKey);
                            } else {
                              newSet.delete(record.portfolioKey);
                            }
                            setSelectedPortfolioKeys(newSet);
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {record.portfolio?.nickname || `Portfolio ${record.portfolioKey}`}
                            {isActivated && (
                              <Chip label="Activated" size="small" color="success" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Account ID: {record.portfolio?.accountId} | Broker: {record.portfolio?.brokerName} | Type: {record.portfolio?.accountType}
                          </Typography>
                        </Box>
                      }
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendModalOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!resendEnvelopeData?.envelopeId) {
                setSnackbar({ open: true, message: 'Missing envelope ID', severity: 'error' });
                return;
              }

              const brokerName = resendEnvelopeData.records?.[0]?.portfolio?.brokerName;
              if (!brokerName) {
                setSnackbar({ open: true, message: 'Missing broker name', severity: 'error' });
                return;
              }

              if (selectedPortfolioKeys.size === 0) {
                setSnackbar({ open: true, message: 'Please select at least one portfolio', severity: 'warning' });
                return;
              }

              try {
                console.log('resendEnvelopeData', Array.from(selectedPortfolioKeys));
                const result = await createEnvelope({
                  variables: {
                    input: {
                      envelopeId: resendEnvelopeData.envelopeId,
                      brokerName: brokerName,
                      portfolioKeys: Array.from(selectedPortfolioKeys),
                    },
                  },
                });

                if (result.data?.LpoaEnvelopeCreate.success) {
                  setSnackbar({ open: true, message: result.data.LpoaEnvelopeCreate.message, severity: 'success' });
                  setResendModalOpen(false);
                  refetch();
                } else {
                  setSnackbar({ open: true, message: result.data?.LpoaEnvelopeCreate.message || 'Failed to resend envelope', severity: 'error' });
                }
              } catch (err) {
                setSnackbar({ open: true, message: 'Error resending envelope: ' + err, severity: 'error' });
              }
            }}
            variant="contained"
            color="secondary"
            disabled={selectedPortfolioKeys.size === 0}
          >
            Resend to Selected ({selectedPortfolioKeys.size})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};